import { format } from "date-fns";
import { ExternalLink, LogOut } from "lucide-react";
import {
  useDelete,
  useGetOne,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

import { useDateLocale } from "../../personal/useDateLocale";
import type {
  Contact,
  PipelineCard,
  PipelineStage,
  Priority,
} from "../../types";
import { AssigneeSelect } from "../AssigneeSelect";
import {
  getFullName,
  getPrimaryEmail,
  getPrimaryPhone,
  splitFullName,
  withPrimaryEmail,
  withPrimaryPhone,
} from "../cardModel";
import { InlineText } from "../InlineText";
import { newItemPosition } from "../positions";
import { PrioritySelect } from "../PrioritySelect";
import { SourcePicker } from "../SourcePicker";
import { TagPicker } from "../TagPicker";
import { useRefreshBoard } from "../useRefreshBoard";
import { CardActivity } from "./CardActivity";
import { CardTasksSection } from "./CardTasksSection";
import { FieldRow } from "./FieldRow";

export interface CardDetailsPanelProps {
  cardId: Identifier;
  stages: PipelineStage[];
  onRemoved: () => void;
}

export const CardDetailsPanel = ({
  cardId,
  stages,
  onRemoved,
}: CardDetailsPanelProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { dateFnsLocale } = useDateLocale();
  const refreshBoard = useRefreshBoard();
  const [update] = useUpdate();
  const [isRemoving, setIsRemoving] = useState(false);

  const { data: card } = useGetOne<PipelineCard>("pipeline_cards", {
    id: cardId,
  });
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: card?.contact_id },
    { enabled: card?.contact_id != null },
  );

  if (!card || !contact) {
    return <Skeleton className="h-64 w-full" />;
  }

  const onError = (error: unknown) => {
    notify(
      error instanceof Error ? error.message : "ra.notification.http_error",
      { type: "error" },
    );
    throw error;
  };

  const saveContact = (data: Partial<Contact>) =>
    update(
      "contacts",
      { id: contact.id, data, previousData: contact },
      { returnPromise: true, onSuccess: () => refreshBoard() },
    ).catch(onError);

  const saveCard = (data: Partial<PipelineCard>) =>
    update(
      "pipeline_cards",
      { id: card.id, data, previousData: card },
      { returnPromise: true, onSuccess: () => refreshBoard() },
    ).catch(onError);

  return (
    <div className="flex flex-col gap-4">
      <InlineText
        value={getFullName(contact)}
        label={translate("crm.operations.fields.name")}
        required
        onSave={(name) => saveContact(splitFullName(name))}
        className="text-base font-semibold"
        displayClassName="text-lg font-semibold"
      />
      {card.stage_entered_at && (
        <p className="-mt-3 px-1 text-xs text-muted-foreground">
          {translate("crm.operations.cards.in_stage_since", {
            date: format(new Date(card.stage_entered_at), "PPp", {
              locale: dateFnsLocale,
            }),
          })}
        </p>
      )}

      <FieldRow label={translate("crm.operations.fields.stage")}>
        <Select
          value={String(card.stage_id)}
          onValueChange={(value) => {
            const stage = stages.find(
              (candidate) => String(candidate.id) === value,
            );
            if (stage) {
              saveCard({ stage_id: stage.id, position: newItemPosition() });
            }
          }}
        >
          <SelectTrigger
            className="h-8 w-full"
            aria-label={translate("crm.operations.fields.stage")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stages.map((stage) => (
              <SelectItem key={stage.id} value={String(stage.id)}>
                {stage.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.tags")}>
        <TagPicker
          value={contact.tags ?? []}
          onChange={(tags) => saveContact({ tags })}
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.email")}>
        <InlineText
          type="email"
          value={getPrimaryEmail(contact.email_jsonb)}
          label={translate("crm.operations.fields.email")}
          onSave={(email) =>
            saveContact({
              email_jsonb: withPrimaryEmail(contact.email_jsonb, email),
            })
          }
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.phone")}>
        <InlineText
          type="tel"
          value={getPrimaryPhone(contact.phone_jsonb)}
          label={translate("crm.operations.fields.phone")}
          onSave={(phone) =>
            saveContact({
              phone_jsonb: withPrimaryPhone(contact.phone_jsonb, phone),
            })
          }
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.assignee")}>
        <AssigneeSelect
          className="h-8 w-full"
          value={contact.sales_id}
          onChange={(salesId) => saveContact({ sales_id: salesId })}
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.source")}>
        <SourcePicker
          value={contact.source ?? null}
          onChange={(source) => saveContact({ source })}
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.priority")}>
        <PrioritySelect
          className="h-8 w-full"
          value={card.priority}
          onChange={(priority: Priority) => saveCard({ priority })}
        />
      </FieldRow>

      <Separator />
      <CardTasksSection contactId={contact.id} salesId={contact.sales_id} />
      <CardActivity contact={contact} />

      <Separator />
      <div className="flex flex-wrap justify-between gap-2 pb-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/contacts/${contact.id}/show`}>
            <ExternalLink />
            {translate("crm.operations.cards.open_contact")}
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive"
          onClick={() => setIsRemoving(true)}
        >
          <LogOut />
          {translate("crm.operations.cards.remove")}
        </Button>
      </div>
      <RemoveCardConfirm
        isOpen={isRemoving}
        card={card}
        name={getFullName(contact)}
        onClose={() => setIsRemoving(false)}
        onRemoved={onRemoved}
      />
    </div>
  );
};

const RemoveCardConfirm = ({
  isOpen,
  card,
  name,
  onClose,
  onRemoved,
}: {
  isOpen: boolean;
  card: PipelineCard;
  name: string;
  onClose: () => void;
  onRemoved: () => void;
}) => {
  const notify = useNotify();
  const refreshBoard = useRefreshBoard();
  const [deleteOne, { isPending }] = useDelete<PipelineCard>();
  return (
    <Confirm
      isOpen={isOpen}
      loading={isPending}
      title="crm.operations.cards.remove_title"
      titleTranslateOptions={{ name }}
      content="crm.operations.cards.remove_description"
      onClose={onClose}
      onConfirm={() =>
        deleteOne(
          "pipeline_cards",
          { id: card.id, previousData: card },
          {
            onSuccess: async () => {
              await refreshBoard();
              notify("crm.operations.cards.removed");
              onClose();
              onRemoved();
            },
          },
        )
      }
    />
  );
};
